import React from "react";
import { Table } from "reactstrap";
import { ProblemId } from "../../interfaces/Status";
import { isAccepted } from "../../utils";
import { countBy, groupBy } from "../../utils/GroupBy";
import { TableColor } from "../../utils/TableColor";
import Submission from "../../interfaces/Submission";
import MergedProblem from "../../interfaces/MergedProblem";
import { useMergedProblemMap } from "../../api/APIClient";

interface Props {
  submissions: Submission[];
  setFilterFunc: (from: number, to: number) => void;
}

const BUCKET_SIZE = 100;
const toBucket = (point: number): number =>
  Math.floor(point / BUCKET_SIZE) * BUCKET_SIZE;

export const getTotalCount = (
  mergedProblems: Map<ProblemId, MergedProblem>
) => {
  const totalCountMap = countBy(Array.from(mergedProblems.values()), (p) =>
    typeof p.point === "number" ? toBucket(p.point) : undefined
  );
  return Array.from(totalCountMap.entries())
    .filter(
      (pair: [number | null | undefined, number]): pair is [number, number] =>
        typeof pair[0] === "number"
    )
    .map(([bucket, count]) => ({ bucket, count }))
    .sort((a, b) => a.bucket - b.bucket);
};
export const getUserPointCounts = (
  mergedProblems: Map<ProblemId, MergedProblem>,
  submissions: Submission[]
) => {
  const acceptedSubmissions = submissions.filter((s) => isAccepted(s.result));
  const acByUserId = groupBy(acceptedSubmissions, (s) => s.user_id);
  return Array.from(acByUserId.entries())
    .map(([userId, userSubmissions]) => {
      const problemIds = new Set(userSubmissions.map((s) => s.problem_id));
      const countByBucket = countBy(Array.from(problemIds), (problemId) => {
        const point = mergedProblems.get(problemId)?.point;
        return typeof point === "number" ? toBucket(point) : undefined;
      });
      return { userId, countByBucket };
    })
    .sort((a, b) => a.userId.localeCompare(b.userId));
};

export const SmallTable: React.FC<Props> = ({ submissions, setFilterFunc }) => {
  const mergedProblemMap =
    useMergedProblemMap().data ?? new Map<ProblemId, MergedProblem>();
  const userPointCountMap = getUserPointCounts(mergedProblemMap, submissions);
  const totalCount = getTotalCount(mergedProblemMap);
  return (
    <Table striped bordered hover responsive>
      <thead>
        <tr>
          <th>Point</th>
          {totalCount.map(({ bucket }) => (
            <th key={bucket}>
              <a
                href={window.location.hash}
                onClick={(): void =>
                  setFilterFunc(bucket, bucket + BUCKET_SIZE - 1)
                }
              >
                {bucket}-
              </a>
            </th>
          ))}
        </tr>
        <tr>
          <th>Total</th>
          {totalCount.map(({ bucket, count }) => (
            <th key={bucket}>{count}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {userPointCountMap.map(({ userId, countByBucket }) => (
          <tr key={userId}>
            <td>{userId}</td>
            {totalCount.map(({ bucket, count }) => (
              <td
                key={bucket}
                className={
                  countByBucket.get(bucket) === count
                    ? TableColor.Success
                    : TableColor.None
                }
              >
                {countByBucket.get(bucket) ?? 0}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
};
